import { Button, Form, Input, Select } from "antd";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { AiOutlineArrowLeft, AiOutlineMail } from "react-icons/ai";
import { BsBuildings, BsPersonBadge, BsPersonPlus } from "react-icons/bs";
import { Link } from "react-router-dom";
import { customerRegister } from "../../redux/rtk/features/user/userSlice";
import SuccessPageAdmin from "./Page/SuccessPageAdmin";
import { getDepartments } from "../department/departmentApis";

const { Option } = Select;

const steps = [
  {
    title: "Enter their email",
    text: "Use the address the new member will sign in with.",
  },
  {
    title: "Choose a role",
    text: "Department Heads manage a department, Coordinators support it.",
  },
  {
    title: "Assign a department",
    text: "Their access is scoped to the department you pick.",
  },
];

const FieldLabel = ({ icon: Icon, children }) => (
  <span className='flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200'>
    <Icon className='text-[#0b5cae]' />
    {children}
  </span>
);

const CustomerRegisterAdmin = () => {
  const [loader, setLoader] = useState(false);
  const [success, setSuccess] = useState(false);
  const [departments, setDepartments] = useState([]); // State to store department data
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  useEffect(() => {
    // Fetch departments when component mounts
    const fetchDepartments = async () => {
      try {
        const data = await getDepartments();
        setDepartments(data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const onFinish = async (values) => {
    try {
      const userTypeMapping = {
        "department head": 1,
        "coordinator": 2,
      };

      const formData = {
        ...values,
        usertype: userTypeMapping[values.usertype],
      };

      const resp = await dispatch(customerRegister(formData));
      setLoader(true);
      if (resp.payload.message === "success") {
        setLoader(false);
        form.resetFields();
        setSuccess(true);
      } else if (resp.payload.message === "Email already registered") {
        setLoader(false);
        form.setFields([
          {
            name: "email",
            errors: ["This email is already registered"],
          },
        ]);
      } else {
        setLoader(false);
      }
    } catch (error) {
      console.log(error.message);
      setLoader(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoader(false);
    console.log("Failed:", errorInfo);
  };

  if (success) {
    return (
      <div className='flex justify-center items-center min-h-[80vh]'>
        <SuccessPageAdmin />
      </div>
    );
  }

  return (
    <div className='min-h-[calc(100vh-102px)] flex items-center justify-center px-4 py-8 bg-gradient-to-br from-slate-50 via-blue-50/40 to-sky-50/60 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'>
      <div className='w-full max-w-5xl'>
        <Link
          to='/admin/setup'
          className='mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#0b5cae] transition-colors'
        >
          <AiOutlineArrowLeft />
          Back to setup
        </Link>

        <div className='grid overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200/70 md:grid-cols-5 dark:bg-slate-800 dark:ring-slate-700'>
          {/* Info panel */}
          <div className='relative overflow-hidden bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] p-8 md:col-span-2 md:p-10 text-white'>
            <div className='absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/10 blur-2xl' />
            <div className='absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-sky-300/20 blur-2xl' />

            <div className='relative'>
              <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm shadow-lg'>
                <BsPersonPlus className='text-3xl' />
              </div>
              <h2 className='mt-6 text-2xl md:text-3xl font-extrabold tracking-tight text-white'>
                Add a new team member
              </h2>
              <p className='mt-2 text-blue-50/90'>
                Invite someone to Campus Pulse and give them the right access
                from day one.
              </p>

              <ol className='mt-8 space-y-5'>
                {steps.map((step, i) => (
                  <li key={step.title} className='flex gap-4'>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-bold'>
                      {i + 1}
                    </span>
                    <div>
                      <p className='font-semibold text-white'>{step.title}</p>
                      <p className='text-sm text-blue-50/80'>{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Form */}
          <div className='p-8 md:col-span-3 md:p-12'>
            <span className='inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'>
              <span className='h-2 w-2 rounded-full bg-blue-500' />
              User Registration
            </span>
            <h1 className='mt-4 text-2xl md:text-3xl font-bold text-slate-800 dark:text-white'>
              Create user account
            </h1>
            <p className='mt-1 mb-8 text-slate-500 dark:text-slate-400'>
              Fill in the details below to register a new user.
            </p>

            <Form
              form={form}
              className='cp-searchable-form'
              layout='vertical'
              name='basic'
              size='large'
              requiredMark={false}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete='off'
            >
              <Form.Item
                label={<FieldLabel icon={AiOutlineMail}>Email address</FieldLabel>}
                name='email'
                rules={[
                  {
                    required: true,
                    message: "Please input email!",
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email!",
                  },
                ]}
              >
                <Input
                  className='rounded-xl'
                  placeholder='name@university.edu'
                />
              </Form.Item>

              <div className='grid gap-x-5 sm:grid-cols-2'>
                <Form.Item
                  label={<FieldLabel icon={BsPersonBadge}>User type</FieldLabel>}
                  name='usertype'
                  rules={[
                    {
                      required: true,
                      message: "Please select user type!",
                    },
                  ]}
                >
                  <Select
                    placeholder='Type or select user type'
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                    notFoundContent={
                      <span className='text-slate-500'>No matching user type</span>
                    }
                  >
                    <Option value='department head'>Department Head</Option>
                    <Option value='coordinator'>Coordinator</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={<FieldLabel icon={BsBuildings}>Department</FieldLabel>}
                  name='department'
                  rules={[
                    {
                      required: true,
                      message: "Please select department!",
                    },
                  ]}
                >
                  <Select
                    placeholder='Type or select department'
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .includes(input.trim().toLowerCase())
                    }
                    notFoundContent={
                      <span className='text-slate-500'>No matching department</span>
                    }
                  >
                    {departments.map((department) => (
                      <Option key={department.id} value={department.id}>
                        {department.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <Form.Item className='mt-4 mb-0'>
                <Button
                  onClick={() => setLoader(true)}
                  block
                  type='primary'
                  htmlType='submit'
                  loading={loader}
                  className='cp-navy-btn h-12 rounded-xl border-0 font-semibold shadow-lg shadow-blue-900/30 hover:opacity-90'
                >
                  Register User
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegisterAdmin;
